import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import { Rating } from '@material-ui/lab';

import { DataPulseContext } from '../contexts/DataPulseContext';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  questionCard: {
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
  },
  questionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  questionNumber: {
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  optionsList: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(3),
  },
  optionItem: {
    padding: theme.spacing(0.5, 0),
  },
}));

const ViewSurvey = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const { currentSurvey, loadSurvey, isLoading } = useContext(DataPulseContext);

  useEffect(() => {
    if (id) {
      loadSurvey(id);
    }
  }, [id]);

  if (isLoading || !currentSurvey) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const getQuestionTypeLabel = (type) => {
    const labels = {
      text: 'Short Text',
      textarea: 'Long Text',
      multiple_choice: 'Multiple Choice',
      checkboxes: 'Checkboxes',
      dropdown: 'Dropdown',
      rating: 'Rating Scale',
      yes_no: 'Yes/No',
      email: 'Email',
      phone: 'Phone Number',
      date: 'Date',
    };
    return labels[type] || type;
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/data-pulse')}
            style={{ marginRight: 16 }}
          >
            Back
          </Button>
          <Typography variant="h5">View Survey</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => history.push(`/data-pulse/edit/${id}`)}
        >
          Edit Survey
        </Button>
      </Box>

      {/* Survey Details */}
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {currentSurvey.name}
            </Typography>
            {currentSurvey.description && (
              <Typography variant="body1" color="textSecondary" paragraph>
                {currentSurvey.description}
              </Typography>
            )}
          </Box>
          <Chip 
            label={currentSurvey.status ? currentSurvey.status.charAt(0).toUpperCase() + currentSurvey.status.slice(1) : 'Draft'} 
            color={currentSurvey.status === 'active' ? 'primary' : 'default'}
          />
        </Box>

        <Divider style={{ margin: '24px 0' }} />

        {/* Welcome Message */}
        {currentSurvey.welcome_message && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Welcome Message</Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
              {currentSurvey.welcome_message}
            </Typography>
          </Box>
        )}

        {/* Thank You Message */}
        {currentSurvey.thank_you_message && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Thank You Message</Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
              {currentSurvey.thank_you_message}
            </Typography>
          </Box>
        )}

        {/* Settings */}
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>Settings</Typography>
          <Typography variant="body2">
            Anonymous Responses: {currentSurvey.allow_anonymous ? 'Allowed' : 'Not Allowed'}
          </Typography>
        </Box>
      </Paper>

      {/* Questions */}
      <Paper className={classes.paper}>
        <Typography variant="h5" gutterBottom>
          Questions ({currentSurvey.questions?.length || 0})
        </Typography>

        {currentSurvey.questions && currentSurvey.questions.length > 0 ? (
          currentSurvey.questions.map((question, index) => (
            <Card key={question.id} className={classes.questionCard}>
              <CardContent>
                <Box className={classes.questionHeader}>
                  <Typography className={classes.questionNumber}>
                    Q{index + 1}.
                  </Typography>
                  <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                      {question.question_text}
                      {question.is_required && (
                        <Chip 
                          label="Required" 
                          size="small" 
                          color="secondary" 
                          style={{ marginLeft: 8 }}
                        />
                      )}
                    </Typography>
                    <Chip 
                      label={getQuestionTypeLabel(question.question_type)} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                </Box>

                {/* Show placeholder if available */}
                {question.placeholder_text && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    Placeholder: "{question.placeholder_text}"
                  </Typography>
                )}

                {/* Show options for multiple choice, checkboxes, dropdown */}
                {['multiple_choice', 'checkboxes', 'dropdown'].includes(question.question_type) && 
                 question.options && question.options.length > 0 && (
                  <Box className={classes.optionsList}>
                    <Typography variant="subtitle2" gutterBottom>Options:</Typography>
                    {question.options.map((option, optIndex) => (
                      <Typography key={option.id} variant="body2" className={classes.optionItem}>
                        • {option.option_text}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Show rating scale */}
                {question.question_type === 'rating' && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Rating Scale: 1 to {question.rating_scale || 5}
                    </Typography>
                    <Rating value={question.rating_scale || 5} max={question.rating_scale || 5} readOnly />
                  </Box>
                )}

                {/* Show min/max for numeric fields */}
                {(question.min_value || question.max_value) && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    {question.min_value && `Min: ${question.min_value}`}
                    {question.min_value && question.max_value && ' | '}
                    {question.max_value && `Max: ${question.max_value}`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <Typography variant="body1" color="textSecondary">
            No questions added yet.
          </Typography>
        )}
      </Paper>
    </div>
  );
};

export default ViewSurvey;
